import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { MovementComponent } from '../components/MovementComponent';
import { AIComponent } from '../components/AIComponent';
import { CombatComponent } from '../components/CombatComponent';
import { HealthComponent } from '../components/HealthComponent';
import { getDirection } from '../../utils/IsometricUtils';

/**
 * AISystem - Basic AI behaviors for beasties
 * Handles following, idle behavior, and state transitions
 */
export class AISystem extends System {
  public readonly requiredComponents = ['transform', 'movement', 'ai'];

  /** Distance to maintain from owner when following */
  private followDistance = 1.5;

  /** Distance at which to stop following (too close) */
  private minFollowDistance = 0.8;

  /** Speed multiplier when catching up to owner */
  private catchUpMultiplier = 1.3;

  /** Time between AI decisions (ms) */
  private decisionInterval = 100;

  /** Track last decision time */
  private lastDecisionTime = 0;

  public init(): void {
    this.lastDecisionTime = Date.now();
  }

  public update(entities: Entity[], delta: number): void {
    const currentTime = Date.now();

    // Throttle AI decisions
    if (currentTime - this.lastDecisionTime < this.decisionInterval) {
      // Still update movement even when not making decisions
      for (const entity of entities) {
        this.updateMovement(entity, delta);
      }
      return;
    }

    this.lastDecisionTime = currentTime;

    for (const entity of entities) {
      const ai = entity.getComponent<AIComponent>('ai');
      if (!ai || !ai.isActive) continue;

      this.processAI(entity, delta);
    }
  }

  /**
   * Process AI for a single entity
   */
  private processAI(entity: Entity, delta: number): void {
    const ai = entity.getComponent<AIComponent>('ai');
    if (!ai) return;

    switch (ai.state) {
      case 'following':
        this.processFollowing(entity);
        break;
      case 'idle':
        this.processIdle(entity, delta);
        break;
      case 'patrol':
        this.processPatrol(entity);
        break;
      case 'combat':
        this.processCombat(entity);
        break;
      default:
        // Other states not implemented yet
        break;
    }

    this.updateMovement(entity, delta);
  }

  /**
   * Process following behavior - move towards owner
   */
  private processFollowing(entity: Entity): void {
    const transform = entity.getComponent<TransformComponent>('transform');
    const movement = entity.getComponent<MovementComponent>('movement');
    const ai = entity.getComponent<AIComponent>('ai');
    const combat = entity.getComponent<CombatComponent>('combat');
    const health = entity.getComponent<HealthComponent>('health');

    if (!transform || !movement || !ai) return;

    // Fainted entities stay in place - don't follow or fight
    if (health?.isFainted) {
      movement.clearTarget();
      movement.velocityX = 0;
      movement.velocityY = 0;
      return;
    }

    // Check for nearby enemies if entity can fight
    if (combat && combat.canInitiateCombat) {
      const enemy = this.findNearestEnemy(entity);
      if (enemy) {
        // Enemy detected, switch to combat
        ai.setState('combat');
        combat.engageTarget(enemy.id);
        return;
      }
    }

    // Find owner entity
    const owner = ai.ownerEntityId ? this.world.getEntity(ai.ownerEntityId) : null;
    if (!owner) {
      // No owner, switch to idle
      ai.setState('idle');
      return;
    }

    const ownerTransform = owner.getComponent<TransformComponent>('transform');
    if (!ownerTransform) return;

    // Calculate distance to owner
    const distance = transform.distanceTo(ownerTransform);

    // Calculate direction to owner
    const dx = ownerTransform.x - transform.x;
    const dy = ownerTransform.y - transform.y;

    // Update facing direction towards owner
    if (dx !== 0 || dy !== 0) {
      transform.facing = getDirection(transform.x, transform.y, ownerTransform.x, ownerTransform.y);
    }

    if (distance > this.followDistance) {
      // Too far - move towards owner
      const dirLength = Math.sqrt(dx * dx + dy * dy);
      if (dirLength > 0) {
        // Set target position (slightly behind owner)
        const targetX = ownerTransform.x - (dx / dirLength) * this.followDistance * 0.8;
        const targetY = ownerTransform.y - (dy / dirLength) * this.followDistance * 0.8;

        movement.setTarget(targetX, targetY);

        // Speed boost if far behind
        if (distance > this.followDistance * 2) {
          movement.speedModifier = this.catchUpMultiplier;
        } else {
          movement.speedModifier = 1;
        }
      }
    } else if (distance < this.minFollowDistance) {
      // Too close - back up slightly
      movement.clearTarget();
      movement.speedModifier = 1;

      // Move away from owner
      if (dx !== 0 || dy !== 0) {
        const dirLength = Math.sqrt(dx * dx + dy * dy);
        const backX = -dx / dirLength;
        const backY = -dy / dirLength;

        // Set velocity directly for backing up
        movement.velocityX = backX * movement.maxSpeed * 0.3;
        movement.velocityY = backY * movement.maxSpeed * 0.3;
      }
    } else {
      // Good distance - stop and idle
      movement.clearTarget();
      movement.speedModifier = 1;
    }
  }

  /**
   * Process idle behavior
   */
  private processIdle(entity: Entity, _delta: number): void {
    const movement = entity.getComponent<MovementComponent>('movement');
    const ai = entity.getComponent<AIComponent>('ai');

    if (!movement || !ai) return;

    // If has owner, consider switching to following
    if (ai.ownerEntityId) {
      const owner = this.world.getEntity(ai.ownerEntityId);
      if (owner) {
        const ownerTransform = owner.getComponent<TransformComponent>('transform');
        const transform = entity.getComponent<TransformComponent>('transform');

        if (ownerTransform && transform) {
          const distance = transform.distanceTo(ownerTransform);

          // If owner moves away, start following
          if (distance > this.followDistance * 1.5) {
            ai.setState('following');
            return;
          }
        }
      }
    }

    // Otherwise, stay idle
    movement.clearTarget();
  }

  /**
   * Process patrol behavior
   */
  private processPatrol(entity: Entity): void {
    const transform = entity.getComponent<TransformComponent>('transform');
    const movement = entity.getComponent<MovementComponent>('movement');
    const ai = entity.getComponent<AIComponent>('ai');

    if (!transform || !movement || !ai) return;

    const waypoint = ai.getNextPatrolPoint();
    if (!waypoint) {
      ai.setState('idle');
      return;
    }

    // Check if reached waypoint
    const dx = waypoint.x - transform.x;
    const dy = waypoint.y - transform.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 0.5) {
      // Reached waypoint, go to next
      ai.advancePatrol();
    } else {
      // Move towards waypoint
      movement.setTarget(waypoint.x, waypoint.y);
    }
  }

  /**
   * Process combat behavior - approach and attack enemies
   */
  private processCombat(entity: Entity): void {
    const transform = entity.getComponent<TransformComponent>('transform');
    const movement = entity.getComponent<MovementComponent>('movement');
    const ai = entity.getComponent<AIComponent>('ai');
    const combat = entity.getComponent<CombatComponent>('combat');
    const health = entity.getComponent<HealthComponent>('health');

    if (!transform || !movement || !ai || !combat) return;

    // If dead or fainted, exit combat
    if (health && (!health.isAlive || health.isFainted)) {
      ai.setState(health.isFainted ? 'following' : 'dead');
      combat.disengage();
      return;
    }

    // If no target or current target is dead/gone/now ally, find a new one
    let needsNewTarget = !combat.targetEntityId;
    if (combat.targetEntityId) {
      const currentTarget = this.world.getEntity(combat.targetEntityId);
      const currentTargetHealth = currentTarget?.getComponent<HealthComponent>('health');
      if (!currentTarget) {
        needsNewTarget = true;
      } else if (currentTargetHealth && !currentTargetHealth.isAlive) {
        needsNewTarget = true;
        combat.disengage(); // Clear the dead target
      } else if (this.areAllies(entity, currentTarget)) {
        // Target became an ally (e.g., was tamed)
        needsNewTarget = true;
        combat.disengage();
      }
    }

    if (needsNewTarget) {
      const target = this.findNearestEnemy(entity);
      if (target) {
        combat.engageTarget(target.id);
        ai.targetId = target.id;
      } else {
        // No enemies, return to following owner
        combat.disengage();
        if (ai.ownerEntityId) {
          ai.setState('following');
        } else {
          ai.setState('idle');
        }
        return;
      }
    }

    // Get target
    const target = this.world.getEntity(combat.targetEntityId!);
    if (!target) {
      combat.disengage();
      ai.setState('following');
      return;
    }

    const targetTransform = target.getComponent<TransformComponent>('transform');
    const targetHealth = target.getComponent<HealthComponent>('health');

    if (!targetTransform) {
      combat.disengage();
      ai.setState('following');
      return;
    }

    // Check if target is dead
    if (targetHealth && !targetHealth.isAlive) {
      combat.disengage();
      ai.setState('following');
      return;
    }

    // Calculate distance to target
    const distance = transform.distanceTo(targetTransform);

    // Update facing direction towards target
    transform.facing = getDirection(
      transform.x,
      transform.y,
      targetTransform.x,
      targetTransform.y
    );

    if (distance > combat.attackRange) {
      // Too far - move towards target
      movement.setTarget(targetTransform.x, targetTransform.y);
      movement.speedModifier = 1;
    } else {
      // In range - stop and attack (CombatSystem handles actual attacks)
      movement.clearTarget();
      movement.velocityX = 0;
      movement.velocityY = 0;
    }
  }

  /**
   * Find nearest enemy entity within detection range
   * For party members, enemies are tagged 'enemy'
   * For enemies, targets are tagged 'party_member' or 'player'
   */
  private findNearestEnemy(entity: Entity): Entity | null {
    const transform = entity.getComponent<TransformComponent>('transform');
    const ai = entity.getComponent<AIComponent>('ai');

    if (!transform || !ai) return null;

    // Determine which tags to look for based on entity type
    const isEnemy = entity.hasTag('enemy');

    // Get all potential targets
    let targets: Entity[] = [];
    if (isEnemy) {
      // Enemies target party members and the player
      targets = [
        ...this.world.getEntitiesByTag('party_member'),
        ...this.world.getEntitiesByTag('player'),
      ];
    } else {
      // Party members target enemies
      targets = this.world.getEntitiesByTag('enemy');
    }

    let nearestTarget: Entity | null = null;
    let nearestDistance = ai.detectionRange;

    for (const target of targets) {
      const targetTransform = target.getComponent<TransformComponent>('transform');
      const targetHealth = target.getComponent<HealthComponent>('health');

      if (!targetTransform) continue;

      // Skip dead or fainted targets
      if (targetHealth && (!targetHealth.isAlive || targetHealth.isFainted)) continue;

      const distance = transform.distanceTo(targetTransform);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestTarget = target;
      }
    }

    return nearestTarget;
  }

  /**
   * Update movement towards target
   */
  private updateMovement(entity: Entity, delta: number): void {
    const transform = entity.getComponent<TransformComponent>('transform');
    const movement = entity.getComponent<MovementComponent>('movement');

    if (!transform || !movement || !movement.canMove) return;

    // If has target, move towards it
    if (movement.targetX !== null && movement.targetY !== null) {
      const dx = movement.targetX - transform.x;
      const dy = movement.targetY - transform.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > 0.1) {
        // Move towards target
        movement.moveInDirection(dx, dy, delta);
      } else {
        // Reached target
        movement.clearTarget();
        movement.applyFriction(delta);
      }
    } else {
      // No target, apply friction
      movement.applyFriction(delta);
    }
  }

  /**
   * Check if two entities are allies (both party members or both enemies)
   */
  private areAllies(entityA: Entity, entityB: Entity): boolean {
    const aIsParty = entityA.hasTag('party_member') || entityA.hasTag('player');
    const bIsParty = entityB.hasTag('party_member') || entityB.hasTag('player');

    const aIsEnemy = entityA.hasTag('enemy');
    const bIsEnemy = entityB.hasTag('enemy');

    // Both on same team = allies
    return (aIsParty && bIsParty) || (aIsEnemy && bIsEnemy);
  }

  public destroy(): void {
    // No cleanup needed
  }
}
